// src/components/general/Tabs/Telefonos/TelefonosTab.jsx
import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import ConfirmDeleteDialog from '../../../security/ConfirmDeleteDialog';
import Modal from '../Catalogos/Telefonos/TelefonosModalTab';
import { ENDPOINTS } from '../../Utils/Endpoints';
import { Models } from '../../Utils/ModelsGeneral';
import sigmaEdit from '../../../Pages/img/Editar.png';
import sigmaDelete from '../../../Pages/img/Borrar.png';
import AlertDialog from '../../../Lms/Components/Shared/AlertDialog';

/* =========================
   Config local del TAB
   ========================= */
const TITLETAB = 'Teléfonos';
const idKey = 'idTelefono';
const nameKey = 'numero';
const fkKey = 'idPersona';

const endpoint = ENDPOINTS.telefonos;       // <— ajusta si tu nombre difiere
const endpointById = ENDPOINTS.telefonoById;    // <— ajusta si difiere

const normalize = Models.telefonos;

const buildPayload = (f, mode) => ({
  ...(mode === 'edit' ? { idTelefono: f.idTelefono } : {}),
  numero: f.numero,
  tipo: f.tipo,
  esPrincipal: !!f.esPrincipal,
});

/* =========================
   Componente
   ========================= */
const PAGE = 5;

export default function TelefonosTab({ fkValue, getDefaults, puedeCrear, puedeEditar, puedeEliminar }) {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [query, setQuery] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState('new');
  const [selectedId, setSelectedId] = useState(null);
  const [editData, setEditData] = useState(null);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState({ id: 0, name: '' });

  const [page, setPage] = useState(1);
  const [editingRowId, setEditingRowId] = useState(null);
  const [alertConfig, setAlertConfig] = useState({ open: false, message: '', title: 'Información', variant: 'info' });

  const triggerAlert = (message, variant = 'error', title = 'Error') => {
    setFormOpen(false);
    setDeleteOpen(false);
    setAlertConfig({ open: true, message, variant, title });
  };

  const httpGetList = async () => axios.get(endpoint);
  const httpPost = (payload) => axios.post(endpoint, payload);
  const httpPutBase = (payload) => axios.put(endpoint, payload);

  const load = async () => {
    setLoading(true);
    try {
      const res = await httpGetList();
      const arr = (res?.data || []).map(normalize);
      setRows(arr);
    } catch (e) {
      console.error(e);
      triggerAlert(`Ocurrió un error al cargar ${TITLETAB}. Inténtelo más tarde.`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => { try { await load(); } catch { } })();
  }, [fkValue]); // eslint-disable-line react-hooks/exhaustive-deps

  const filteredByText = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      Object.values(r).join(' ').toLowerCase().includes(q)
    );
  }, [rows, query]);

  const filtered = useMemo(() => {
    if (!fkKey) return filteredByText;
    const fkNum = Number(fkValue);
    return filteredByText.filter((r) => Number(r?.[fkKey]) === fkNum);
  }, [filteredByText, fkValue]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE));
  useEffect(() => { if (page > totalPages) setPage(1); }, [totalPages, page]);

  const paged = useMemo(() => {
    const start = (page - 1) * PAGE;
    return filtered.slice(start, start + PAGE);
  }, [filtered, page]);

  const buildWithFk = (formData, mode) => {
    const p = {
      ...(mode === 'edit' ? { [idKey]: selectedId } : {}),
      ...formData,
    };
    if (fkKey && fkValue != null && p[fkKey] == null) p[fkKey] = fkValue;
    return p;
  };

  const onNew = () => {
    setFormMode('new');
    setSelectedId(null);
    const defaults = (typeof getDefaults === 'function')
      ? getDefaults(fkValue)
      : (fkKey ? { [fkKey]: fkValue } : {});
    setEditData(defaults);
    setFormOpen(true);
    setEditingRowId(null);
  };

  const onEdit = async (id) => {
    setFormMode('edit');
    setSelectedId(id);
    const localRow = rows.find(r => r[idKey] === id);
    setEditData(localRow ?? { [idKey]: id, ...(fkKey ? { [fkKey]: fkValue } : {}) });
    setFormOpen(true);
    setEditingRowId(id);
  };

  const onAskDelete = (row) => {
    const id = row[idKey];
    const label = row?.[nameKey] ? String(row[nameKey]) : `${TITLETAB} #${id}`;
    setDeleteTarget({ id, name: label });
    setDeleteOpen(true);
  };

  const onConfirmDelete = async () => {
    try {
      setLoading(true);
      await axios.delete(endpointById(deleteTarget.id));
      setDeleteOpen(false);
      await load();
    } catch (e) {
      console.error(e);
      triggerAlert('Ocurrió un error al eliminar. Inténtelo más tarde.');
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePrincipal = async (item) => {
    if (item.esPrincipal) return; // Ya es principal, no hacemos nada 
    
    try {
      setLoading(true);
      
      // 1. Desmarcar todos los demás
      const others = filtered.filter((r) => r.esPrincipal && r[idKey] !== item[idKey]);
      if (others.length > 0) {
        await Promise.all(others.map(async (other) => {
          const pOther = {
            ...buildPayload(other, 'edit'),
            [idKey]: other[idKey],
            [fkKey]: fkValue,
            esPrincipal: false,
          };
          await httpPutBase(pOther);
        }));
      }

      // 2. Marcar el actual como principal
      const payload = {
        ...buildPayload(item, 'edit'),
        [idKey]: item[idKey],
        [fkKey]: fkValue,
        esPrincipal: true,
      };
      await httpPutBase(payload);
      
      await load();
    } catch (e) {
      console.error(e);
      triggerAlert('Ocurrió un error al cambiar el estado principal.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitFromModal = async (formFromModal, modeFromModal) => {
    const mode = modeFromModal || formMode;
    try {
      setLoading(true);
      const baseForm = { ...(formFromModal || {}) };
      if (fkKey && fkValue != null && baseForm[fkKey] == null) baseForm[fkKey] = fkValue;
      if (mode === 'edit' && selectedId != null && baseForm[idKey] == null) baseForm[idKey] = selectedId;

      const payload = buildWithFk(baseForm, mode);

      // --- Si este es principal, desmarcar los demás ---
      if (payload.esPrincipal) {
        const currentId = (mode === 'edit') ? selectedId : null;
        // Filtramos de 'filtered' (que ya son de esta persona)
        const others = filtered.filter((r) => r.esPrincipal && r[idKey] !== currentId);

        await Promise.all(others.map(async (other) => {
          const pOther = {
            ...buildPayload(other, 'edit'),
            [idKey]: other[idKey],
            [fkKey]: fkValue,
            esPrincipal: false,
          };
          await httpPutBase(pOther);
        }));
      }
      // ------------------------------------------------
      let newId = null;
      if (mode === 'new') {
        const res = await httpPost(payload);
        newId = res?.data?.idTelefono ?? (typeof res?.data === 'number' ? res.data : null);
      } else {
        await httpPutBase(payload);
        newId = payload.idTelefono;
      }

      if (newId) setEditingRowId(newId);
      setFormOpen(false);
      await load();
    } catch (e) {
      console.error(e);
      const msg = e.response?.data?.message || 'Ocurrió un error al guardar. Inténtelo más tarde.';
      triggerAlert(msg);
    } finally {
      setLoading(false);
    }
  };

  const PageBtn = ({ label, onClick, active, disabled }) => (
    <button
      className={`roles-page-btn ${active ? 'is-active' : ''} ${disabled ? 'is-disabled' : ''}`}
      onClick={onClick}
      disabled={disabled}
      type="button"
    >
      {label}
    </button>
  );

  const pages = (() => {
    const maxToShow = 5;
    let start = Math.max(1, page - 2);
    let end = Math.min(totalPages, start + maxToShow - 1);
    start = Math.max(1, end - maxToShow + 1);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  })();

  return (
    <div className="mini-crud">
      <div className="mini-topbar">
        <h3 className="roles-title" style={{ margin: 0 }}>{TITLETAB}</h3>
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            className="roles-search"
            placeholder="Buscar…"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setPage(1); }}
          />
          {puedeCrear && (
            <button className="btn-action" onClick={onNew}>＋ Nuevo</button>
          )}
        </div>
      </div>

      <div className="roles-table-wrap">
        <div className="roles-table-scroller">
          <table className="roles-table">
            <thead>
              <tr>
                <th>Número</th>
                <th>Tipo</th>
                <th>Principal</th>
                {puedeEditar && <th style={{ textAlign: 'center' }}>Editar</th>}
                {puedeEliminar && <th style={{ textAlign: 'center' }}>Borrar</th>}
              </tr>
            </thead>
            <tbody>
              {paged.map((row) => (
                <tr key={row[idKey]} className={editingRowId === row[idKey] ? 'rk-row-active' : ''}>
                  <td>{row.numero}</td>
                  <td>{row.tipo}</td>
                  <td style={{ verticalAlign: 'middle', textAlign: 'center' }}>
                    <div
                      onClick={() => !loading && handleTogglePrincipal(row)}
                      style={{
                        width: '34px',
                        height: '18px',
                        backgroundColor: row.esPrincipal ? '#10b981' : '#cbd5e1',
                        borderRadius: '12px',
                        position: 'relative',
                        cursor: loading ? 'not-allowed' : (row.esPrincipal ? 'default' : 'pointer'),
                        transition: 'background-color 0.2s',
                        flexShrink: 0,
                        opacity: loading ? 0.6 : 1,
                        margin: '0 auto'
                      }}
                      title={row.esPrincipal ? "Este teléfono es el principal" : "Marcar como principal"}
                    >
                      <div style={{
                        width: '12px',
                        height: '12px',
                        backgroundColor: 'white',
                        borderRadius: '50%',
                        position: 'absolute',
                        top: '3px',
                        left: row.esPrincipal ? '19px' : '3px',
                        transition: 'left 0.2s',
                        boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                      }} />
                    </div>
                  </td>
                  {puedeEditar && (
                    <td style={{ textAlign: 'center' }}>
                      <button
                        type="button"
                        className="rk-iconbtn rk-iconbtn--edit"
                        onClick={() => onEdit(row[idKey])}
                        aria-label="Editar"
                        title="Editar"
                      >
                        <img src={sigmaEdit} alt="" className="rk-ico" />
                      </button>
                    </td>
                  )}
                  {puedeEliminar && (
                    <td style={{ textAlign: 'center' }}>
                      <button
                        type="button"
                        className="rk-iconbtn rk-iconbtn--del"
                        onClick={() => onAskDelete(row)}
                        aria-label="Borrar"
                        title="Borrar"
                      >
                        <img src={sigmaDelete} alt="" className="rk-ico" />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
              {!loading && paged.length === 0 && (
                <tr><td colSpan={3 + (puedeEditar ? 1 : 0) + (puedeEliminar ? 1 : 0)}>Sin resultados.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="roles-pagination" style={{ marginTop: 8 }}>
        <div />
        <div className="roles-pagination-right">
          <PageBtn label="‹" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} />
          {pages.map((n) => (<PageBtn key={n} label={n} onClick={() => setPage(n)} active={n === page} />))}
          <PageBtn label="›" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} />
        </div>
      </div>

      {formOpen && (
        <Modal
          open={formOpen}
          mode={formMode}
          data={editData}
          fkValue={fkValue}
          fields={[]}
          formDefaults={{}}
          title={TITLETAB}
          idKey={idKey}
          onClose={() => setFormOpen(false)}
          onSubmit={handleSubmitFromModal}
          inline={false}
        />
      )}

      <ConfirmDeleteDialog
        open={deleteOpen}
        roleName={deleteTarget.name}
        onClose={() => setDeleteOpen(false)}
        onConfirm={onConfirmDelete}
      />

      <AlertDialog
        open={alertConfig.open}
        title={alertConfig.title}
        message={alertConfig.message}
        variant={alertConfig.variant}
        onClose={() => setAlertConfig({ ...alertConfig, open: false })}
      />
    </div>
  );
}
